import React, { useEffect, useState } from "react";
import { getClientProfitability } from "../../../services/productionProfitabilityService";
import ScoreBadge from "./ScoreBadge";
import AlertList from "./AlertList";
import KpiCard from "./KpiCard";
import RecommendationCard from "./RecommendationCard";
import ServiceBreakdownTable from "./ServiceBreakdownTable";

/**
 * Formateurs null-safe
 */
function money(v) {
  if (v === null || v === undefined) return "—";
  return new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD" }).format(v);
}

function pct(v) {
  if (v === null || v === undefined) return "—";
  return `${Number(v).toFixed(1)}%`;
}

/**
 * Panel de rentabilité client
 * Affiche score, KPIs, alertes, recommandation, breakdown par service
 */
export default function ClientProfitabilityPanel({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      if (!clientId) return;
      
      try {
        setLoading(true);
        setError("");
        const res = await getClientProfitability(clientId);
        setData(res);
      } catch (err) {
        console.error("Erreur chargement rentabilité client:", err);
        setError("Impossible de charger les données de rentabilité.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [clientId]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="text-center text-gray-500">
          <div className="animate-pulse">📊 Chargement des données de rentabilité...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <div className="text-red-700">⚠️ {error}</div>
      </div>
    );
  }

  if (!data) return null;

  const { totals, score, alerts, recommendation, service_breakdown } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-2xl font-bold text-gray-900">💼 Rentabilité Client</div>
            <div className="text-sm text-gray-500 mt-1">{data.client_name}</div>
          </div>
          <ScoreBadge score={score.letter} numeric={score.numeric} />
        </div>

        {/* KPI Row */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mt-6">
          <KpiCard label="Profit brut" value={money(totals.gross_profit)} />
          <KpiCard label="Marge" value={pct(totals.margin_percent)} />
          <KpiCard label="Profit / visite" value={money(totals.profit_per_visit)} />
          <KpiCard label="Profit / heure" value={money(totals.profit_per_hour)} />
          <KpiCard label="Visites" value={totals.visits_completed} />
          <KpiCard label="Écart temps" value={pct(totals.time_variance_percent)} />
        </div>
      </div>

      {/* Recommandation */}
      <RecommendationCard recommendation={recommendation} />

      {/* Alertes */}
      {alerts && alerts.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="text-lg font-semibold mb-4">⚠️ Alertes</div>
          <AlertList alerts={alerts} />
        </div>
      )}

      {/* Breakdown par service */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="text-lg font-semibold mb-4">📊 Rentabilité par service</div>
        <ServiceBreakdownTable rows={service_breakdown} />
      </div>

      {/* Stats supplémentaires */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="text-lg font-semibold mb-4">📊 Statistiques détaillées</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-sm text-gray-500">Types de service</div>
            <div className="text-xl font-bold">{totals.service_types_count}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Revenu / heure</div>
            <div className="text-xl font-bold">{money(totals.revenue_per_hour)}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Revenu total</div>
            <div className="text-xl font-bold">{money(totals.revenue_total)}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Coût total</div>
            <div className="text-xl font-bold">{money(totals.total_cost)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
